import clsx from 'clsx';
import type { RunSummary } from '@/api/types';
import { money, when } from '@/lib/format';
import { StatusPill } from './StatusPill';

interface Props {
  runs: RunSummary[];
  selectedId: number | null;
  onSelect: (id: number) => void;
  onRetry: (id: number) => void;
  retryingId: number | null;
}

export function RunsTable({ runs, selectedId, onSelect, onRetry, retryingId }: Props) {
  if (runs.length === 0) {
    return (
      <div className="panel p-12 text-center">
        <p className="text-sm text-zinc-400">No runs here yet.</p>
        <p className="mt-1 text-xs text-zinc-600">
          Runs appear when the Ontraport signature automation posts to the webhook.
        </p>
      </div>
    );
  }

  return (
    <div className="panel overflow-x-auto">
      <table className="w-full min-w-[720px] text-left text-sm whitespace-nowrap">
        <thead className="border-b border-zinc-800 text-xs tracking-wide text-zinc-500 uppercase">
          <tr>
            <th className="px-4 py-2.5 font-medium">Client</th>
            <th className="px-4 py-2.5 font-medium">FY</th>
            <th className="px-4 py-2.5 font-medium">TFN</th>
            <th className="px-4 py-2.5 font-medium">Status</th>
            <th className="px-4 py-2.5 text-right font-medium">Funds</th>
            <th className="px-4 py-2.5 text-right font-medium">Balance</th>
            <th className="px-4 py-2.5 font-medium">Updated</th>
            <th className="px-4 py-2.5" />
          </tr>
        </thead>
        <tbody className="divide-y divide-zinc-800/70">
          {runs.map((run) => (
            <tr
              key={run.id}
              onClick={() => onSelect(run.id)}
              className={clsx(
                'cursor-pointer transition-colors hover:bg-zinc-800/40',
                selectedId === run.id && 'bg-zinc-800/60',
              )}
            >
              <td className="px-4 py-2.5">
                <div className="font-medium text-zinc-100">{run.contactName || '—'}</div>
                <div className="font-mono text-xs text-zinc-500">{run.contactEmail}</div>
              </td>
              <td className="px-4 py-2.5 font-mono text-zinc-400">{run.taxYear}</td>
              <td className="px-4 py-2.5 font-mono text-zinc-400">
                {run.tfnSuffix ? `•••${run.tfnSuffix}` : '—'}
              </td>
              <td className="px-4 py-2.5">
                <StatusPill status={run.status} />
                {run.lastError && (
                  <div className="mt-1 max-w-xs truncate text-xs text-zinc-500" title={run.lastError}>
                    {run.lastError}
                  </div>
                )}
              </td>
              <td className="px-4 py-2.5 text-right font-mono text-zinc-300">
                {run.status === 'Succeeded' ? run.fundCount : '—'}
              </td>
              <td className="px-4 py-2.5 text-right font-mono text-zinc-300">
                {run.status === 'Succeeded' ? money(run.totalBalance) : '—'}
              </td>
              <td className="px-4 py-2.5 text-zinc-500">{when(run.completedAt ?? run.createdAt)}</td>
              <td className="px-4 py-2.5 text-right">
                <button
                  className="btn px-2 py-1 text-xs"
                  disabled={retryingId === run.id}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRetry(run.id);
                  }}
                >
                  {retryingId === run.id ? '…' : 'Re-run'}
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
